import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageContainer } from "@/layouts/PageContainer";
import { Search } from "@/components/ui/Search";
import { Filter } from "@/components/ui/Filter";
import { DataTable } from "@/components/tables/DataTable";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Avatar } from "@/components/ui/Avatar";
import { Drawer } from "@/components/ui/Drawer";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { Dropdown } from "@/components/ui/Dropdown";
import { useDebounce } from "@/hooks/useDebounce";
import { useSetUserStatus, useUser, useUsersList } from "@/features/users/useUsers";
import { toast } from "@/store/toast.store";
import { formatDate, formatINR, formatNumber } from "@/utils/format";
import type { AccountStatus, AppUser } from "@/types/domain";

const STATUS_OPTIONS: { label: string; value: AccountStatus }[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Paused", value: "PAUSED" },
  { label: "Flagged", value: "FLAGGED" },
  { label: "Blocked", value: "BLOCKED" },
];

export default function Users() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search);

  const query = {
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    status: (status || undefined) as AccountStatus | undefined,
  };
  const users = useUsersList(query);
  const setUserStatus = useSetUserStatus();

  const columns = useMemo<ColumnDef<AppUser, unknown>[]>(
    () => [
      {
        header: "Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Avatar name={row.original.name ?? row.original.phone} size={28} />
            <span className="font-medium">{row.original.name ?? "—"}</span>
          </div>
        ),
      },
      { header: "Phone", accessorKey: "phone" },
      { header: "Trips", accessorFn: (u) => formatNumber(u.totalTrips) },
      { header: "Rating", accessorFn: (u) => `★ ${u.rating.toFixed(1)}` },
      { header: "Status", cell: ({ row }) => <StatusBadge status={row.original.status} /> },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => (
          <Dropdown
            trigger={<span className="text-ink-faint">⋯</span>}
            options={
              row.original.status === "ACTIVE"
                ? [
                    { label: "Pause", value: "PAUSED" },
                    { label: "Block", value: "BLOCKED", danger: true },
                  ]
                : [{ label: "Reactivate", value: "ACTIVE" }]
            }
            onSelect={(value) => {
              setUserStatus.mutate(
                { id: row.original.id, status: value as AccountStatus },
                { onSuccess: () => toast.success("User status updated") },
              );
            }}
          />
        ),
      },
    ],
    [setUserStatus],
  );

  return (
    <PageContainer
      title={`Riders · ${formatNumber(users.data?.total ?? 0)}`}
      description="Riders, trip history, and support flags."
      actions={
        <>
          <Search value={search} onChange={setSearch} placeholder="Search name, phone, email…" />
          <Filter label="Status" value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        </>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={users.data?.items ?? []}
          isLoading={users.isLoading}
          error={users.error}
          onRetry={() => users.refetch()}
          onRowClick={(u) => setSelectedId(u.id)}
          rowKey={(u) => u.id}
          emptyTitle="No users found"
        />
        {users.data && (
          <Pagination page={page} pageSize={20} total={users.data.total} onPageChange={setPage} />
        )}
      </div>

      <UserDetailDrawer userId={selectedId} onClose={() => setSelectedId(null)} />
    </PageContainer>
  );
}

function UserDetailDrawer({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const user = useUser(userId ?? undefined);

  return (
    <Drawer open={!!userId} onClose={onClose} title={user.data?.name ?? "User"} subtitle={user.data?.phone}>
      {user.isLoading && <LoadingState label="Loading user…" />}
      {user.error && <ErrorState error={user.error} onRetry={() => user.refetch()} />}
      {user.data && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <StatusBadge status={user.data.status} />
            <span className="text-sm text-ink-muted">
              {formatNumber(user.data.totalTrips)} trips · joined {formatDate(user.data.createdAt)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-ink-faint">Lifetime spend</p>
              <p className="font-medium text-ink">{formatINR(user.data.lifetimeSpend)}</p>
            </div>
            <div>
              <p className="text-xs text-ink-faint">Paid rides</p>
              <p className="font-medium text-ink">{formatNumber(user.data.paidRides)}</p>
            </div>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-ink">Recent rides</h4>
            {user.data.recentRides.length === 0 && (
              <p className="text-xs text-ink-faint">No rides yet.</p>
            )}
            <ul className="flex flex-col gap-2">
              {user.data.recentRides.map((r, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium text-ink">{r.code}</p>
                    <p className="text-xs text-ink-faint">{r.pickup?.name ?? r.pickup?.area ?? "—"}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-ink">
                      {r.fare ? formatINR(r.fare.total) : "—"}
                    </p>
                    <StatusBadge status={r.status} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Drawer>
  );
}
